import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { isString } from 'lodash';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const createDraftProformaInvoice = async ({ payload }) => {
  const profileId = payload?.accounting_supplier_party?.profile_id;
  if (!profileId) {
    throw new Error('Supplier Profile ID is required to create a sales invoice');
  }
  const response = await apiClient.post(`/proforma-invoices/${profileId}/draft`, payload);
  return response.data;
};

export const useCreateDraftProformaInvoiceHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const { snackbarMessages } = useTranslatedMessages();
  const piSnacks = snackbarMessages?.proforma_invoices ?? {};

  const mutation = useMutation({
    mutationFn: createDraftProformaInvoice,
    onSuccess: (data, variables, context) => {
      dispatch(
        showSnackbar({ message: piSnacks?.draft_created_success ?? 'Draft proforma invoice created successfully.' }),
      );
      queryClient.invalidateQueries({ queryKey: ['proforma-invoices'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response.status === 400) {
          setError(error.response.data.errors);
          return;
        }
        dispatch(showSnackbar({ message: error.response.data.message, severity: 'error' }));
      } else {
        dispatch(
          showSnackbar({
            message: isString(error.message) ? error.message : JSON.stringify(error.message),
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (payload) => {
    mutation.mutate({ payload });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
